/**
 * The analytic swell field — the single source of truth for water height.
 *
 * This is a sum of Gerstner (trochoidal) waves. It runs here on the CPU for buoyancy,
 * projectile water entry, wake anchoring and tests, and the IDENTICAL function runs in
 * the ocean vertex shader (src/shaders/ocean.vert) driven by the very same component
 * array uploaded as uniforms. One definition, two evaluators — so what the player sees
 * and what the simulation computes can never drift apart.
 *
 * The FFT detail layer (render-side only, amplitude-capped) adds visual chop on top and
 * deliberately carries NO physics — see IMPLEMENTATION_PLAN.md C1.
 *
 * Gerstner form (GPU Gems 1, ch. 1):
 *   theta = w * dot(D, xz) + phi * t + offset
 *   P.xz  = xz + sum( Q_i * A_i * D_i * cos(theta_i) )
 *   P.y   =      sum(        A_i * sin(theta_i) )
 * with w = 2*pi/L, deep-water phase speed c = sqrt(g*L/(2*pi)), phi = w*c.
 */

import { GRAVITY, TAU, clamp, clamp01, lerp, type Vec3 } from './math';
import { Rng } from './rng';

/**
 * Number of resolved wave components. Must match WAVE_COUNT in oceanShared.glsl.
 *
 * Twelve components proved the CPU/GPU contract and added cross swell, but storm frames
 * still exposed a few dominant families as a repeated diamond quilt. Sixteen lets the
 * same total energy be divided among more unequal, crossing wave trains, so no one pair
 * draws the shape of the whole ocean. This remains cheap at buoyancy sample counts.
 */
export const WAVE_COUNT = 16;

export interface WaveComponent {
  /** Unit direction on the XZ plane. */
  dirX: number;
  dirZ: number;
  /** Metres, crest to mean. */
  amplitude: number;
  /** Metres, crest to crest. */
  wavelength: number;
  /** Angular wavenumber, 2*pi/wavelength. */
  w: number;
  /** Temporal angular frequency, w * phaseSpeed. */
  phi: number;
  /** Horizontal pinch factor for this component (already divided by w*A*N). */
  q: number;
  /** Static phase offset so the field does not start flat. */
  offset: number;
}

export interface WaveField {
  components: WaveComponent[];
  /** Sea state 3..7 this field was built for. */
  seaState: number;
  /** Wind heading in radians (direction the wind blows TOWARD). */
  windDir: number;
  /** Significant wave height, derived. Useful for gameplay modifiers and tests. */
  significantHeight: number;
  /** Sum of amplitudes — the theoretical crest maximum. */
  maxAmplitude: number;
}

export interface WaveFieldParams {
  seaState: number;
  windDir: number;
  seed?: string;
}

/**
 * Geometric wavelength ratio between successive components.
 * Deliberately not a simple fraction, so the summed field has no visible repeat
 * over the playable area (the classic Gerstner tiling artefact).
 */
/**
 * The sea is COMPOSED, not sampled from a statistical spectrum.
 *
 * Earlier fields spread energy across four semi-random systems. Statistically plausible,
 * visually wrong: energy scattered over many bearings and bands means nothing coheres,
 * and the surface reads as ripple even when individual numbers are storm-sized.
 *
 * What the direction and reference imagery actually describe is a CROSS SEA: two large,
 * coherent wave trains converging on the platform from different bearings, colliding
 * diagonally. So that is what we build:
 *
 *   - Train A: the dominant system. One primary component plus four satellites at small
 *     angular/wavelength offsets. Satellites are what give a train LONG but finite
 *     crests — they modulate the crest line slowly so it reads as an organic swath
 *     hundreds of metres long, not an infinite corduroy ribbon.
 *   - Train B: a second system ~66 degrees away, slightly shorter and weaker.
 *   - Detail set: six small components for surface variety only. Deliberately weak:
 *     short-scale GEOMETRY is what fragmented earlier seas into ripples. Fine texture
 *     belongs to the shading layers (detail normals, foam), not the silhouette.
 *
 * Where the two trains cross, displacement superposes: the surface reaches heights and
 * pinch (Jacobian fold) that neither train reaches alone, so breaking concentrates in
 * large PATCHES at the intersections — exactly the colliding-foam look asked for — and
 * it emerges from the physics rather than from painting foam at chosen spots.
 *
 * Wavelength multipliers are deliberate irrational-looking values, chosen so no pair of
 * components sits near a small-integer ratio (the tiling guard in the tests enforces
 * this: near-commensurate pairs beat visibly over open water).
 */
const TRAIN_SEPARATION = 1.15;

/**
 * Global slow-down on every component's temporal frequency. Deep-water dispersion is
 * physically correct but reads as hurried from a fixed vantage 100m up, where there is
 * no parallax to sell the distance. Relative speeds are preserved: long trains still
 * outrun short detail.
 */
const WAVE_TIME_SCALE = 0.68;

interface ComponentSpec {
  /** Wavelength as a fraction of the sea state's peak wavelength. */
  wavelengthMul: number;
  /** Relative amplitude within the field; absolute scale is solved to target Hs. */
  weight: number;
  /** Bearing offset from its train's centreline, radians. */
  angleOffset: number;
  /** Which system the component belongs to. */
  train: 'a' | 'b' | 'detail';
}

const COMPONENT_SPECS: readonly ComponentSpec[] = [
  // Sorted by wavelength (the packing and tests expect long -> short); membership of
  // the A train, B train or detail set is carried by the `train` field, not adjacency.
  // Trains use WIDE wavelength splits so same-bearing members beat into GROUPS: crests
  // arrive in sets with lulls between, so visible spacing varies instead of repeating.
  // Detail is sparse in the 30-80m band, whose regular rows wove the old diamond grid.
  { wavelengthMul: 1.0, weight: 1.0, angleOffset: 0.0, train: 'a' },
  { wavelengthMul: 0.882, weight: 0.26, angleOffset: 0.1, train: 'a' },
  { wavelengthMul: 0.795, weight: 0.75, angleOffset: 0.0, train: 'b' },
  { wavelengthMul: 0.647, weight: 0.55, angleOffset: 0.055, train: 'a' },
  { wavelengthMul: 0.615, weight: 0.2, angleOffset: 0.09, train: 'b' },
  { wavelengthMul: 0.5465, weight: 0.45, angleOffset: -0.06, train: 'b' },
  { wavelengthMul: 0.512, weight: 0.33, angleOffset: -0.045, train: 'a' },
  { wavelengthMul: 0.3885, weight: 0.26, angleOffset: 0.05, train: 'b' },
  { wavelengthMul: 0.3, weight: 0.06, angleOffset: 0.34, train: 'detail' },
  { wavelengthMul: 0.2235, weight: 0.05, angleOffset: -0.52, train: 'detail' },
  { wavelengthMul: 0.166, weight: 0.042, angleOffset: 0.7, train: 'detail' },
  { wavelengthMul: 0.1225, weight: 0.036, angleOffset: -0.82, train: 'detail' },
  { wavelengthMul: 0.0875, weight: 0.03, angleOffset: 0.48, train: 'detail' },
  { wavelengthMul: 0.065, weight: 0.026, angleOffset: -0.62, train: 'detail' },
  { wavelengthMul: 0.048, weight: 0.022, angleOffset: 0.56, train: 'detail' },
  { wavelengthMul: 0.037, weight: 0.019, angleOffset: -0.44, train: 'detail' },
];

const MIN_BASE_WAVELENGTH = 70;
const MAX_BASE_WAVELENGTH = 310;

/** Sea state -> peak amplitude/wavelength, directional spread and steepness. */
function seaStateProfile(
  seaState: number,
): { amp: number; peakWavelength: number; spread: number; steep: number } {
  const s = clamp(seaState, 2, 8);
  const severity = clamp01((s - 3) / 4);
  // Calm 3 -> heavy 7. Amplitude of the LONGEST component; others scale down from it.
  // Growth is deliberately nonlinear: a normal rough day must not already look like a
  // once-a-decade storm, while sea state 7 still reaches North Atlantic double digits.
  // Target significant height, metres. Amplitudes are solved to hit this AFTER the
  // spectral shape is chosen, so shape can be retuned without moving wave height (and
  // without breaking the height assertions in tests/sim/waves.test.ts).
  const amp = lerp(1.0, 16.5, Math.pow(severity, 1.72));
  // Peak wavelength grows with fetch and severity — a storm is not merely a taller
  // version of a moderate sea, it is a LONGER one, and that length is what makes swells
  // sweep across the field of view rather than mill about in it.
  const peakWavelength = lerp(
    MIN_BASE_WAVELENGTH,
    MAX_BASE_WAVELENGTH,
    Math.pow(severity, 1.25),
  );
  // Rougher seas are more confused: wider directional spread.
  const spread = lerp(0.28, 0.78, severity);
  // Steeper, sharper crests as the sea builds.
  // Total slope budget. Kept strictly below 1 because the new steepness distribution
  // concentrates it: the summed horizontal-displacement derivative equals `steep`, so
  // staying under 1 GUARANTEES the surface never self-intersects even in the worst-case
  // alignment of every component. (The old equal-slope-per-component scheme gave the
  // dominant slab only 1/16th of the budget — why big waves had gentle faces.)
  const steep = lerp(0.45, 0.98, severity);
  return { amp, peakWavelength, spread, steep };
}

export function buildWaveField(params: WaveFieldParams): WaveField {
  const { seaState, windDir } = params;
  const rng = new Rng(params.seed ?? `waves:${seaState.toFixed(2)}`);
  const {
    amp: targetSignificantHeight,
    peakWavelength,
    spread,
    steep,
  } = seaStateProfile(seaState);

  const components: WaveComponent[] = [];
  let sumAmp = 0;
  let sumAmpSq = 0;

  const trainABearing = windDir - TRAIN_SEPARATION / 2;
  const trainBBearing = windDir + TRAIN_SEPARATION / 2;

  for (let i = 0; i < WAVE_COUNT; i++) {
    const spec = COMPONENT_SPECS[i] as ComponentSpec;
    const wavelength = peakWavelength * spec.wavelengthMul;

    // Small jitter only. The composed structure IS the design; heavy per-component
    // randomisation is what previously dissolved it back into statistical mush.
    const amplitude = spec.weight * rng.range(0.92, 1.08);

    const base =
      spec.train === 'a' ? trainABearing
      : spec.train === 'b' ? trainBBearing
      : windDir;
    const detailSpread = spec.train === 'detail' ? spread * 0.55 : 1;
    const angle = base + spec.angleOffset * detailSpread + rng.jitter(0.02);

    const w = TAU / wavelength;
    const phaseSpeed = Math.sqrt((GRAVITY * wavelength) / TAU);
    // Members of a train travel together; jittering their speeds apart dissolves the
    // train within a minute of simulation. The trains differ from each other naturally
    // through dispersion, and the detail set may drift freely.
    const speedJitter = spec.train === 'detail' ? rng.range(0.9, 1.1) : rng.range(0.985, 1.015);
    const phi = w * phaseSpeed * WAVE_TIME_SCALE * speedJitter;

    components.push({
      dirX: Math.sin(angle),
      dirZ: Math.cos(angle),
      amplitude,
      wavelength,
      w,
      phi,
      // q filled in below, once we know the amplitude sum.
      q: 0,
      offset: rng.range(0, TAU),
    });

    sumAmp += amplitude;
    sumAmpSq += amplitude * amplitude;
  }

  // The spectral shape above sets the RATIOS between components; this scales the whole
  // set so the field hits its target significant height. Decoupling shape from height
  // means the peak can be sharpened or the falloff retuned without silently changing how
  // big the sea is — and without breaking the height assertions in the tests.
  const shapedHeight = 4 * Math.sqrt(sumAmpSq / 2);
  const heightSolve = shapedHeight > 1e-6 ? targetSignificantHeight / shapedHeight : 1;
  sumAmp = 0;
  sumAmpSq = 0;
  for (const c of components) {
    c.amplitude *= heightSolve;
    sumAmp += c.amplitude;
    sumAmpSq += c.amplitude * c.amplitude;
  }

  // Steepness distributed by amplitude share: Q_i = steep / (w_i * sumAmp), so each
  // component's slope contribution is steep * A_i / sumAmp and the total is exactly
  // `steep`. The big trains therefore carry the slope — faces of ~10 degrees on a lone
  // crest, twice that where the trains cross — while the detail set stays shallow. The
  // old Q_i = steep/(w_i A_i N) gave every band an EQUAL slope share, which is why a
  // 16-band sea always looked gentle no matter how tall it was.
  for (const c of components) {
    c.q = steep / (c.w * sumAmp);
  }

  return {
    components,
    seaState,
    windDir,
    // H_s = 4 * sqrt(m0), m0 = sum(A^2)/2 for a sum of sinusoids.
    significantHeight: 4 * Math.sqrt(sumAmpSq / 2),
    maxAmplitude: sumAmp,
  };
}

/**
 * Raw Gerstner evaluation at a GRID point — exactly what the vertex shader computes.
 * Note `x`/`z` are the undisplaced grid coordinates; the result is displaced in all
 * three axes. To ask "how high is the water at world position X" use waterHeightAt().
 */
export function waveDisplacement(field: WaveField, x: number, z: number, t: number, out?: Vec3): Vec3 {
  const r = out ?? { x: 0, y: 0, z: 0 };
  let dx = 0;
  let dy = 0;
  let dz = 0;
  const comps = field.components;
  for (let i = 0; i < comps.length; i++) {
    const c = comps[i] as WaveComponent;
    const theta = c.w * (c.dirX * x + c.dirZ * z) + c.phi * t + c.offset;
    const s = Math.sin(theta);
    const co = Math.cos(theta);
    const qa = c.q * c.amplitude * co;
    dx += c.dirX * qa;
    dz += c.dirZ * qa;
    dy += c.amplitude * s;
  }
  r.x = x + dx;
  r.z = z + dz;
  // Envelope keyed on the DISPLACED world radius, matching the shader exactly.
  r.y = dy * geometryEnvelope(Math.hypot(r.x, r.z));
  return r;
}

/**
 * Geometric displacement envelope — MUST match geometryEnvelope() in oceanShared.glsl.
 *
 * Beyond ~2.6 km a swell crest moves less than a pixel on screen, while the radial grid's
 * far rings are hundreds of metres apart — displacing them just aliases and shimmers. So
 * vertical displacement fades out with distance from the rig, and the surface read is
 * carried by foam, detail normals and haze instead (exactly as the reference imagery
 * looks at range). Applying the identical envelope on both sides keeps buoyancy honest:
 * a ship at 5 km floats on the same flat water the player sees.
 *
 * Horizontal (choppy) displacement is deliberately NOT faded — it is at most a couple of
 * metres, invisible at range, and leaving it unattenuated keeps the inversion well-posed.
 */
export const GEOM_FULL_RANGE = 2600;
export const GEOM_FADE_RANGE = 8200;

export function geometryEnvelope(distFromOrigin: number): number {
  const t = clamp01((distFromOrigin - GEOM_FULL_RANGE) / (GEOM_FADE_RANGE - GEOM_FULL_RANGE));
  return 1 - t * t * (3 - 2 * t);
}

/** Vertical-only displacement at a grid point. */
function verticalDisplacement(field: WaveField, x: number, z: number, t: number): number {
  let dy = 0;
  const comps = field.components;
  for (let i = 0; i < comps.length; i++) {
    const c = comps[i] as WaveComponent;
    const theta = c.w * (c.dirX * x + c.dirZ * z) + c.phi * t + c.offset;
    dy += c.amplitude * Math.sin(theta);
  }
  return dy;
}

/**
 * Newton iterations for the displacement inversion. Convergence is quadratic, and the
 * Jacobian reuses sin/cos we already evaluate, so each pass is nearly free at the sample
 * counts buoyancy actually needs. Three passes hold sub-millimetre accuracy even at storm
 * sea states, where the taller, steeper waves make the solve materially harder.
 * (A plain fixed-point iteration needs 5+ passes for the same result, because Gerstner
 * steepness pushes its contraction factor close to 1.)
 */
const INVERSION_ITERATIONS = 3;

/**
 * Recover the undisplaced grid coordinate whose Gerstner displacement lands on world (x,z).
 * Solves F(g) = g + d(g) - x = 0 by Newton's method with the analytic 2x2 Jacobian.
 */
function inverseGridPoint(
  field: WaveField,
  x: number,
  z: number,
  t: number,
): { gx: number; gz: number } {
  let gx = x;
  let gz = z;
  const comps = field.components;

  for (let iter = 0; iter < INVERSION_ITERATIONS; iter++) {
    let dx = 0;
    let dz = 0;
    // Jacobian of (g + d(g)); starts at the identity.
    let j00 = 1;
    let j01 = 0;
    let j11 = 1;

    for (let i = 0; i < comps.length; i++) {
      const c = comps[i] as WaveComponent;
      const theta = c.w * (c.dirX * gx + c.dirZ * gz) + c.phi * t + c.offset;
      const sn = Math.sin(theta);
      const cs = Math.cos(theta);
      const qa = c.q * c.amplitude;
      dx += c.dirX * qa * cs;
      dz += c.dirZ * qa * cs;
      // d/dg of (q*A*D*cos(theta)) = -q*A*w*D*sin(theta) * D
      const k = -qa * c.w * sn;
      j00 += c.dirX * c.dirX * k;
      j01 += c.dirX * c.dirZ * k;
      j11 += c.dirZ * c.dirZ * k;
    }

    const fx = gx + dx - x;
    const fz = gz + dz - z;
    const det = j00 * j11 - j01 * j01;

    if (Math.abs(det) < 1e-9) {
      // Degenerate (should not happen while the loop guard holds) — fall back a step.
      gx -= fx;
      gz -= fz;
    } else {
      gx -= (j11 * fx - j01 * fz) / det;
      gz -= (-j01 * fx + j00 * fz) / det;
    }
  }

  return { gx, gz };
}

/**
 * Water surface height at a WORLD position.
 *
 * Because Gerstner waves move points horizontally, the vertex that renders above
 * world (x,z) started somewhere else on the grid. We recover that origin (above), then
 * evaluate the vertical displacement there. This is what makes floating objects sit
 * exactly on the rendered surface rather than drifting through crests.
 */
export function waterHeightAt(field: WaveField, x: number, z: number, t: number): number {
  const g = inverseGridPoint(field, x, z, t);
  return verticalDisplacement(field, g.gx, g.gz, t) * geometryEnvelope(Math.hypot(x, z));
}

/** Surface normal at a world position, via central differences on the inverted height. */
export function waterNormalAt(field: WaveField, x: number, z: number, t: number, eps = 0.75): Vec3 {
  const hL = waterHeightAt(field, x - eps, z, t);
  const hR = waterHeightAt(field, x + eps, z, t);
  const hD = waterHeightAt(field, x, z - eps, t);
  const hU = waterHeightAt(field, x, z + eps, t);
  const nx = hL - hR;
  const nz = hD - hU;
  const ny = 2 * eps;
  const len = Math.hypot(nx, ny, nz);
  return { x: nx / len, y: ny / len, z: nz / len };
}

/**
 * Vertical velocity of the surface at a world position (m/s).
 * Used so floating hulls inherit heave rather than teleporting between frames.
 */
export function waterVelocityAt(field: WaveField, x: number, z: number, t: number, dt = 1 / 30): number {
  return (waterHeightAt(field, x, z, t + dt) - waterHeightAt(field, x, z, t - dt)) / (2 * dt);
}

/**
 * Pack the field for the GPU: 2 x vec4 per component.
 *   slot A: dirX, dirZ, amplitude, w
 *   slot B: phi, q, offset, (unused)
 * The shader reconstructs the same theta and therefore the same surface.
 */
export function packWavesForShader(field: WaveField): Float32Array {
  const data = new Float32Array(WAVE_COUNT * 8);
  for (let i = 0; i < WAVE_COUNT; i++) {
    const c = field.components[i] as WaveComponent;
    const o = i * 8;
    data[o + 0] = c.dirX;
    data[o + 1] = c.dirZ;
    data[o + 2] = c.amplitude;
    data[o + 3] = c.w;
    data[o + 4] = c.phi;
    data[o + 5] = c.q;
    data[o + 6] = c.offset;
    data[o + 7] = 0;
  }
  return data;
}

/**
 * Sample the field at several points and return an averaged plane fit.
 * This is the buoyancy primitive: bigger hulls average over a longer baseline and so
 * ignore chop, exactly as a real vessel does (IMPLEMENTATION_PLAN.md C1).
 */
export interface BuoyancySample {
  height: number;
  /** Rotation about the vessel's lateral axis, radians. Bow up is positive. */
  pitch: number;
  /** Rotation about the vessel's longitudinal axis, radians. */
  roll: number;
}

export function sampleBuoyancy(
  field: WaveField,
  x: number,
  z: number,
  heading: number,
  halfLength: number,
  halfBeam: number,
  t: number,
): BuoyancySample {
  const fx = Math.sin(heading);
  const fz = Math.cos(heading);
  // Starboard axis = forward rotated -90 degrees about Y.
  const sx = fz;
  const sz = -fx;

  const hBow = waterHeightAt(field, x + fx * halfLength, z + fz * halfLength, t);
  const hStern = waterHeightAt(field, x - fx * halfLength, z - fz * halfLength, t);
  const hPort = waterHeightAt(field, x - sx * halfBeam, z - sz * halfBeam, t);
  const hStbd = waterHeightAt(field, x + sx * halfBeam, z + sz * halfBeam, t);
  const hMid = waterHeightAt(field, x, z, t);

  // Weight the centre sample so long hulls ride the swell rather than the ends.
  const height = (hBow + hStern + hPort + hStbd + 2 * hMid) / 6;
  const pitch = Math.atan2(hBow - hStern, 2 * halfLength);
  const roll = Math.atan2(hStbd - hPort, 2 * halfBeam);
  return { height, pitch, roll };
}
